import {
  Bone,
  CardSkeleton,
  FieldSkeleton,
  HeaderSkeleton,
  Line,
  PanelSkeleton,
  placeholderKeys,
  SkeletonRegion,
  TableSkeleton,
} from '@ems/ui/patterns/skeleton-blocks'

const FILTER_KEYS = placeholderKeys('filter', 3)

export function RegistrationsSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton
        title="w-36"
        description="w-96"
        actions={['h-9 w-32', 'h-9 w-24', 'h-9 w-44']}
      />
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-64 flex-1 gap-2">
          <Bone className="h-9 flex-1" />
          <Bone className="h-9 w-20" />
        </div>
        {FILTER_KEYS.map((key) => (
          <Bone key={key} className="h-9 w-44" />
        ))}
      </div>
      <TableSkeleton
        rows={8}
        card={{ title: 'w-36', description: null }}
        columns={[
          { cell: 'mono', widths: ['w-24', 'w-20'], primary: true },
          { cell: 'two-line', widths: ['w-32', 'w-44', 'w-40', 'w-36'] },
          { cell: 'badge', widths: ['w-20', 'w-24'] },
          { cell: 'badge', widths: ['w-16', 'w-20'] },
          { cell: 'badge', widths: ['w-20', 'w-16'], header: 'w-20' },
          { cell: 'muted', widths: ['w-28', 'w-20'] },
          { cell: 'text', widths: ['w-20', 'w-24'], align: 'end' },
          { cell: 'button', widths: ['w-32'], header: null, align: 'end', shrink: true },
        ]}
      />
      <Bone className="h-9 w-28 self-center" />
    </SkeletonRegion>
  )
}

function ParticipantsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-2">
        <Line size="base" width="w-28" />
        <Line width="w-16" />
      </div>
      <div className="bg-card min-w-0 rounded-lg border border-(--border)">
        <div className="flex items-center gap-3 p-4 md:px-6">
          <div className="flex min-h-11 flex-1 items-center gap-3">
            <Bone className="size-4" />
            <div className="flex flex-col gap-1">
              <Line width="w-32" />
              <Line size="xs" width="w-24" />
            </div>
          </div>
          <Bone className="size-11 shrink-0 sm:size-9" />
        </div>
        <div className="flex flex-col gap-6 border-t border-(--border) p-4 md:p-6">
          <FieldSkeleton control="select" label="w-20" />
          <Line width="w-64" />
        </div>
      </div>
      <Bone className="h-9 w-36 self-start" />
    </div>
  )
}

export function NewRegistrationSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-44" description="w-96" />
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,48rem)_20rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <PanelSkeleton title="w-20" description="w-72">
            <div className="grid gap-4 md:grid-cols-2">
              <FieldSkeleton label="w-12" />
              <FieldSkeleton label="w-12" />
              <FieldSkeleton label="w-14" />
              <FieldSkeleton label="w-16" control="select" />
              <FieldSkeleton label="w-32" className="md:col-span-2" />
            </div>
          </PanelSkeleton>
          <ParticipantsSkeleton />
        </div>
        <PanelSkeleton title="w-24" className="hidden gap-5 md:p-6 xl:flex">
          <Line width="w-40" />
          <Line width="w-32" />
          <div className="flex flex-col gap-2 border-t pt-4">
            <Line width="w-full" />
            <Line width="w-full" />
          </div>
          <Bone className="h-9 w-full" />
        </PanelSkeleton>
      </div>
    </SkeletonRegion>
  )
}

export function ImportRegistrationsSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-48" description="w-80" actions={['h-9 w-40']} />
      <PanelSkeleton title={null} className="max-w-2xl">
        <FieldSkeleton control="select" label="w-28" help="w-72" />
        <FieldSkeleton label="w-32" help="w-full" />
        <Bone className="h-9 w-20 self-start" />
      </PanelSkeleton>
    </SkeletonRegion>
  )
}

export function AddParticipantsSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-40" description="w-80" />
      <div className="flex max-w-3xl flex-col gap-6">
        <ParticipantsSkeleton />
        <Bone className="h-9 w-40 self-start" />
      </div>
    </SkeletonRegion>
  )
}

const AXIS_KEYS = placeholderKeys('axis', 3)
const SUMMARY_KEYS = placeholderKeys('summary', 2)

export function RegistrationSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-36" description="w-80" actions={['h-9 w-24', 'h-9 w-32']} />
      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-3">
        {AXIS_KEYS.map((key) => (
          <CardSkeleton key={key}>
            <div className="flex flex-col items-start gap-2 p-3">
              <Line size="xs" width="w-20" />
              <Bone className="h-[22px] w-24" />
            </div>
          </CardSkeleton>
        ))}
      </div>
      <TableSkeleton
        rows={3}
        card={{ title: 'w-28', description: 'w-40', action: 'h-8 w-36' }}
        columns={[
          { cell: 'text', widths: ['w-32', 'w-40'], primary: true },
          { cell: 'text', widths: ['w-44', 'w-36'] },
          { cell: 'text', widths: ['w-28', 'w-24'] },
          { cell: 'text', widths: ['w-24', 'w-20'] },
          { cell: 'text', widths: ['w-32', 'w-28'] },
          { cell: 'text', widths: ['w-24'], align: 'end' },
          { cell: 'button', widths: ['w-16'], header: null, align: 'end', shrink: true },
        ]}
      />
      <div className="grid gap-6 md:grid-cols-2">
        {SUMMARY_KEYS.map((key) => (
          <PanelSkeleton key={key} title="w-24" className="gap-3">
            <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2">
              <Line width="w-20" />
              <Line width="w-24" />
              <Line width="w-12" />
              <Line width="w-20" />
              <Line width="w-16" />
              <Line width="w-28" />
            </div>
          </PanelSkeleton>
        ))}
      </div>
      <PanelSkeleton title="w-20" className="gap-3">
        <Line width="w-80" />
        <Line width="w-64" />
      </PanelSkeleton>
    </SkeletonRegion>
  )
}
